import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiTarget,
  FiEye,
  FiHeart,
  FiAward,
  FiUsers,
  FiBook,
} from "react-icons/fi";
import { settingsAPI } from "../services/api";
import useScrollAnimation from "../hooks/useScrollAnimation";

const fallbackLeadership = [
  {
    name: "Dr. Ramesh Kumar Sharma",
    role: "Principal",
    description:
      "PhD in Education, 25+ years of experience in educational leadership.",
  },
  {
    name: "Mrs. Sunita Thapa",
    role: "Vice Principal",
    description:
      "Masters in Education Administration, dedicated to student welfare.",
  },
  {
    name: "Mr. Bikram Prasad",
    role: "Academic Director",
    description: "PhD in Curriculum Development, expert in modern pedagogy.",
  },
];

const fallbackAchievements = [
  {
    title: "ISA by British Council",
    desc: "Outstanding development of international dimension in the curriculum",
  },
  {
    title: "District Education Office Award",
    desc: "Outstanding result in SEE and SLC examinations",
  },
  {
    title: "Local Government Appreciation",
    desc: "Letter of appreciation for outstanding results in board exams",
  },
  {
    title: "Consistently High Pass Rate",
    desc: "Consistently high SEE/SLC results every year",
  },
];

const achievementIcons = [FiAward, FiUsers, FiBook, FiAward];

const getInitials = (name) =>
  name
    ? name
        .split(" ")
        .filter(Boolean)
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "??";

const About = () => {
  const [leadership, setLeadership] = useState(fallbackLeadership);
  const [achievements, setAchievements] = useState(fallbackAchievements);

  const { ref: missionRef, isVisible: missionVisible } = useScrollAnimation();
  const { ref: leaderRef, isVisible: leaderVisible } = useScrollAnimation();
  const { ref: achieveRef, isVisible: achieveVisible } = useScrollAnimation();

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        const s = res.data?.data?.settings;
        if (s?.leadership?.length > 0) setLeadership(s.leadership);
        if (s?.achievements?.length > 0) setAchievements(s.achievements);
      })
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero Banner */}
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Know Us Better
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            About Us
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Discover our story, values, and the people who make Gargi
            Academy exceptional
          </p>
        </motion.div>
      </div>

      {/* School History */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="section-subtitle mb-2">Our History</p>
              <h2 className="section-title mb-6">
                A Journey of Excellence Since 2000
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Sited in a serene and tranquil environment, BDEBSS is a
                  premier and time-tested academic hub for top-notch quality
                  education and all-round development for students in Rupandehi,
                  Nepal. Since its inception in 2000 A.D, BDEBSS has carved out
                  a niche as the pinnacle of the best +2 school in Rupandehi
                  District.
                </p>
                <p>
                  BDEBSS has renovated and refurbished its facilities time and
                  again and upholds a techno-friendly and congenial academic
                  milieu conducive to teaching-learning activities. It boasts a
                  holistic approach enriching students' learning experience
                  through practical and student-centered teaching — modern,
                  resourceful, innovative and enterprising.
                </p>
                <p>
                  Today, with 1312 students, 43 teaching faculty and 20
                  non-teaching staff, BDEBSS stands as a testament to what
                  passionate educators and a supportive community can achieve,
                  guiding the avenues for the future of today's generation.
                </p>
              </div>
            </motion.div>

            {/* Timeline */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {[
                {
                  year: "2000",
                  event: "BDEBSS founded in Kanchan Rural Municipality, Rupandehi by 40 founder members",
                  color: "bg-primary-500",
                },
                {
                  year: "2005",
                  event: "Expanded to Secondary School level with modern infrastructure",
                  color: "bg-secondary-500",
                },
                {
                  year: "2010",
                  event: "Added High School (+2) program — Management stream",
                  color: "bg-primary-500",
                },
                {
                  year: "2015",
                  event: "ISA award by British Council for international curriculum excellence",
                  color: "bg-secondary-500",
                },
                {
                  year: "2020",
                  event: "Launched AV-enabled smart classrooms and happiness classes",
                  color: "bg-primary-500",
                },
                {
                  year: "2024",
                  event: "District Education Office award for outstanding SEE/SLC results",
                  color: "bg-secondary-500",
                },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4 items-start">
                  <div
                    className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
                  >
                    {item.year.slice(2)}
                  </div>
                  <div className="pt-2">
                    <p className="font-bold text-primary-500 text-sm">
                      {item.year}
                    </p>
                    <p className="text-gray-600 text-sm">{item.event}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={missionRef}
            initial={{ opacity: 0, y: 20 }}
            animate={missionVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Our Foundation</p>
            <h2 className="section-title">Mission, Vision & Values</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FiTarget,
                title: "Our Mission",
                color: "bg-blue-50 border-blue-100 text-blue-500",
                desc: "Education for peace Creation and Human Development — instilling discipline, nurturing decency, and inculcating moral values with ardor for learning through edge-cut labs, ultra-modern technology, and industry-experienced faculty.",
              },
              {
                icon: FiEye,
                title: "Our Vision",
                color: "bg-primary-50 border-primary-100 text-primary-500",
                desc: "To unfold students' innate potential by encouraging initiative, futuristic outlook, and cultural consciousness — preparing highly qualified human resources needed for the global market of the 21st century.",
              },
              {
                icon: FiHeart,
                title: "Our Values",
                color:
                  "bg-secondary-50 border-secondary-100 text-secondary-600",
                desc: "Ethics, team spirit, and community service are strongly imbibed in our students. We cater holistic development — physical, mental, and emotional — through co-curricular and extra-curricular activities.",
              },
            ].map((card, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                animate={missionVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                className="bg-white border rounded-3xl p-8 text-center shadow-sm card-hover"
              >
                <div
                  className={`w-16 h-16 ${card.color} border rounded-2xl flex items-center justify-center mx-auto mb-5`}
                >
                  <card.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-primary-500 font-serif mb-4">
                  {card.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {card.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* School at a Glance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="section-subtitle mb-2">Quick Facts</p>
            <h2 className="section-title">School at a Glance</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { label: "Established", value: "2000 A.D" },
              { label: "Nature", value: "Private Instruction" },
              { label: "Location", value: "Lumbini Province, Rupandehi District, Kanchan Rural Municipality, Ward No. 5" },
              { label: "Founder Members", value: "40" },
              { label: "Regulating Bodies", value: "Nepal Government, Ministry of Education, Science and Technology" },
              { label: "Classes", value: "Play Group to Class 12" },
              { label: "No. of Students", value: "1312" },
              { label: "Extra-curricular Classes", value: "Happiness Classes, Dance & Music, Taekwondo" },
              { label: "Core Faculty", value: "Management (Finance, Computer, Business, Mathematics, Hotel Management)" },
              { label: "Teaching Faculty", value: "43" },
              { label: "Non-Teaching Faculty", value: "20" },
              { label: "Teacher–Student Ratio", value: "1 : 29" },
              { label: "Medium of Teaching", value: "English" },
              { label: "Audio Visual Facilities", value: "Yes" },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4 bg-gray-50 border border-gray-100 rounded-2xl p-4">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-secondary-600 uppercase tracking-wide mb-0.5">{item.label}</p>
                  <p className="text-gray-800 text-sm font-medium">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Share Members */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="section-subtitle mb-2">संस्थापक शेयर सदस्य</p>
            <h2 className="section-title">Founder Share Members</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              { en: "Lakshmikanta Paudel",    np: "लक्ष्मीकान्त पौडेल" },
              { en: "Tara Prasad Sharma",      np: "तारा प्रसाद शर्मा" },
              { en: "Bishnu Narayan Shrestha", np: "बिष्णु नारायण श्रेष्ठ" },
              { en: "Gir Bahadur Thapa",       np: "गिर बहादुर थापा" },
              { en: "Lekhnath Paudel",         np: "लेखनाथ पौडेल" },
              { en: "Liladhar Dhakal",         np: "लिलाधर ढकाल" },
              { en: "Liladhar Sharma",         np: "लिलाधर शर्मा" },
              { en: "Pharsuiram Panta",        np: "फर्सुराम पन्त" },
              { en: "Atmaaram Paudel",         np: "आत्माराम पौडेल" },
              { en: "Chak B. Thapa Magar",     np: "चक ब. थापा मगर" },
              { en: "Sur B. Fauja",            np: "स्व.सुर ब. फौजा" },
              { en: "Hemavati Thapa",          np: "हेमवती थापा" },
              { en: "Khumananda Acharya",      np: "खुमानन्द आचार्य" },
              { en: "Kaladhar Sharma",         np: "कलाधर शर्मा" },
              { en: "Bam B. Gurung",           np: "बम ब. गुरुङ्ग" },
              { en: "Numa Datta Adhikari",     np: "नुमा दत्त अधिकारी" },
              { en: "Nabaraj Khanal",          np: "नवराज खनाल" },
              { en: "Saraswati K.C.",          np: "सरस्वती के.सी" },
              { en: "Motilal Dhakal",          np: "मोतीलाल ढकाल" },
              { en: "Tulasa Ku. Ghimire",      np: "तुलसा कु. घिमिरे" },
              { en: "Thaneshwar Upadhyaya",    np: "थानेश्वर उपाध्याय" },
              { en: "Krishna Pra. Upadhyaya",  np: "कृष्ण प्रा.उपाध्याय" },
              { en: "Parvati Devi Parajuli",   np: "पार्वती देवी पराजुली" },
              { en: "Dinanath Acharya",        np: "दिनानाथ आचार्य" },
              { en: "Thaneshwar Acharya",      np: "थानेश्वर आचार्य" },
              { en: "Ayodhya Tharu",           np: "आयोध्या थारु" },
              { en: "Padma Paudel",            np: "पदमा पौडेल" },
              { en: "Ishwari Gautam",          np: "इश्वरी गौतम" },
              { en: "Gaura Thapa",             np: "गौरा थापा" },
              { en: "Kavita Paudel",           np: "कविता पौडेल" },
              { en: "Keshavraj Paudel",        np: "केशवराज पौडेल" },
              { en: "Lu Maya Gurung",          np: "लु माया गुरुङ्ग" },
            ].map((member, idx) => {
              const initials = member.en
                .split(" ")
                .filter(Boolean)
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);
              const colors = [
                "bg-primary-500", "bg-secondary-500", "bg-blue-500",
                "bg-indigo-500", "bg-teal-500", "bg-purple-500",
              ];
              const bg = colors[idx % colors.length];
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: (idx % 12) * 0.05 }}
                  viewport={{ once: true }}
                  className="flex flex-col items-center text-center"
                >
                  <div className={`w-16 h-16 rounded-full ${bg} flex items-center justify-center text-white text-lg font-bold mb-3 shadow-md`}>
                    {initials}
                  </div>
                  <p className="text-gray-800 text-xs font-semibold leading-snug mb-0.5">{member.np}</p>
                  <p className="text-gray-400 text-xs leading-snug">{member.en}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership — dynamic */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={leaderRef}
            initial={{ opacity: 0, y: 20 }}
            animate={leaderVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Our Leadership</p>
            <h2 className="section-title">Meet the Administration</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {leadership.map((person, idx) => (
              <motion.div
                key={person._id || idx}
                initial={{ opacity: 0, y: 30 }}
                animate={leaderVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                className="bg-gray-50 border border-gray-100 rounded-3xl p-8 text-center card-hover"
              >
                <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-5 flex-shrink-0">
                  {person.photo?.url ? (
                    <img
                      src={person.photo.url}
                      alt={person.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-primary-500 flex items-center justify-center text-white text-2xl font-bold">
                      {getInitials(person.name)}
                    </div>
                  )}
                </div>
                <h3 className="font-bold text-primary-500 text-lg font-serif mb-1">
                  {person.name}
                </h3>
                <p className="text-secondary-600 text-sm font-medium mb-3">
                  {person.role}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {person.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements — dynamic */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={achieveRef}
            initial={{ opacity: 0, y: 20 }}
            animate={achieveVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Our Pride</p>
            <h2 className="section-title">Achievements & Recognition</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {achievements.map((item, idx) => {
              const Icon = achievementIcons[idx % achievementIcons.length];
              return (
                <motion.div
                  key={item._id || idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={achieveVisible ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white border border-gray-100 rounded-2xl p-6 text-center card-hover"
                >
                  <div className="w-12 h-12 bg-secondary-50 border border-secondary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-secondary-600" />
                  </div>
                  <h4 className="font-bold text-primary-500 text-sm mb-2">
                    {item.title}
                  </h4>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    {item.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
